import {
  createEntityAdapter,
  createSlice,
  EntityState,
} from "@reduxjs/toolkit";
import {
  ExportRecoveryDetailsRequest,
  RecoveryDetails,
} from "../../../api/recovery";
import { RootState } from "../../../app/store";
import {
  fetchRecoveryDetailsThunk,
  upsertRecoveryDetailsThunk,
} from "./recoveryThunk";

export interface RecoveryState {
  recovery: EntityState<RecoveryDetails, string>;
}

const recoveryAdapter = createEntityAdapter<RecoveryDetails, string>({
  selectId: (record) => record.loan_reference_id,
});

const initialState: RecoveryState = {
  recovery: recoveryAdapter.getInitialState({}),
};

const applyRecoveryPayload = (
  state: RecoveryState,
  payload: ExportRecoveryDetailsRequest
) => {
  recoveryAdapter.upsertMany(state.recovery, payload.recovery_details || []);
};

const recoverySlice = createSlice({
  name: "recovery",
  initialState,
  reducers: {
    clearRecoveryState: (state) => {
      recoveryAdapter.removeAll(state.recovery);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRecoveryDetailsThunk.pending, (state) => {
        recoveryAdapter.removeAll(state.recovery); 
      })
      .addCase(fetchRecoveryDetailsThunk.fulfilled, (state, action) => {
        applyRecoveryPayload(state, action.payload);
      })
      .addCase(upsertRecoveryDetailsThunk.fulfilled, (state, action) => {
        applyRecoveryPayload(state, action.payload);
      });
  },
});

export const { clearRecoveryState } = recoverySlice.actions;

export const recoveryReducer = recoverySlice.reducer;

const recoverySelectors = recoveryAdapter.getSelectors(
  (state: RootState) => state.recoveryData.recovery
);

export const selectAllRecoveryDetails = recoverySelectors.selectAll;
export const selectRecoveryByLrId = recoverySelectors.selectById;
export const selectRecoveryTotal = recoverySelectors.selectTotal;

export const selectRecoveryDetailsByLoanReferenceId = (loan_reference_id?: string) => {
  return (state: RootState) => {
    return loan_reference_id? selectRecoveryByLrId(state, loan_reference_id): null;
  }
}